// /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/components/Counter.tsx
import React, { useEffect, useRef, useState } from "react";
import { useLanguage } from "../lang/index.lang.tsx";
import { useBreakpoints } from "../hooks/Breakpoints.tsx";
import Container from "../hooks/Container.tsx";

interface CounterItem {
  key: string;
  endValue: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
}

interface CounterProps {
  items: CounterItem[];
  sectionTitle: string;
  mainTitle: string;
  id?: string;
}

const Counter: React.FC<CounterProps> = ({ 
  items, 
  sectionTitle, 
  mainTitle,
  id
}) => {
  // useLanguage() Hook liefert aktuelle Sprache UND Ãœbersetzungen
  const { t, currentLanguage } = useLanguage(); // <-- currentLanguage ist korrekt!
  const { current } = useBreakpoints();
  const [isVisible, setIsVisible] = useState(false);
  // ✅ MIT lazy initialization - items.map(() => 0) läuft nur 1x beim Mount
  const [counts, setCounts] = useState<number[]>(() => items.map(() => 0));
  const sectionRef = useRef<HTMLDivElement>(null);

  const isMobile = current === "mobile";
  const isTablet = current === "tablet";

  // Mapping: Sprache â†’ Locale fÃ¼r toLocaleString()
  const getLocale = (lang: string): string => {
    const localeMap: Record<string, string> = {
      'de': 'de-DE',    // Deutsch â†’ 5.000,50
      'en': 'en-US',    // Englisch â†’ 5,000.50
      'fr': 'fr-FR',    // FranzÃ¶sisch â†’ 5 000,50
      'es': 'es-ES',    // Spanisch â†’ 5.000,50
      'it': 'it-IT',    // Italienisch â†’ 5.000,50
    };
    return localeMap[lang] || 'de-DE'; // Fallback auf Deutsch
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const animationDuration = 2500;
    const frameRate = 60;
    const totalFrames = (animationDuration / 1000) * frameRate;

    // Ease-In: Langsam am Anfang, schnell am Ende
    const easeIn = (t: number): number => {
      return t * t * t * t;
    };

    items.forEach((item, index) => {
      let frame = 0;

      const timer = setInterval(() => {
        frame++;
        const progress = frame / totalFrames;
        const easedProgress = easeIn(progress);
        
        setCounts((prev) => {
          const newCounts = [...prev];
          newCounts[index] = Math.min(easedProgress * item.endValue, item.endValue);
          return newCounts;
        });

        if (frame >= totalFrames) {
          clearInterval(timer);
          setCounts((prev) => {
            const newCounts = [...prev];
            newCounts[index] = item.endValue;
            return newCounts;
          });
        }
      }, 1000 / frameRate);
    });
  }, [isVisible, items]);

  const gridCols = isMobile
    ? "grid-cols-2"
    : isTablet
    ? "grid-cols-2"
    : "grid-cols-4";

  // Dynamische Locale basierend auf User-Spracheinstellung
  const locale = getLocale(currentLanguage);

  // Ãœbersetzung von deutschen AbkÃ¼rzungen ins Englische
  const translateAbbreviation = (text: string | undefined, lang: string): string => {
    if (!text || lang === 'de') return text || '';
    
    const abbreviationMap: Record<string, string> = {
      'Mrd.': 'Bn',      // Milliarde â†’ Billion
      'Mrd': 'Bn',       // Milliarde â†’ Billion (ohne Punkt)
      'Mio.': 'M',       // Million â†’ M
      'Mio': 'M',        // Million â†’ M (ohne Punkt)
      'Tsd.': 'K',       // Tausend â†’ K
      'Tsd': 'K',        // Tausend â†’ K (ohne Punkt)
    };

    let translated = text;
    Object.entries(abbreviationMap).forEach(([de, en]) => {
      translated = translated.replace(new RegExp(de, 'g'), en);
    });
    
    return translated;
  };

  return (
    <section
      id={id}
      ref={sectionRef}
      className="w-full bg-black text-white mb-[150px] md:mb-[150px] lg:mb-[300px]"
    >
      <Container>
        <div className="mb-12">
          <p className="text-white text-xl md:text-1xl lg:text-2xl mb-2">
            {sectionTitle}
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-7xl font-bold leading-tight mb-8">
            {mainTitle}
          </h2>
        </div>

        <div className={`grid ${gridCols} gap-x-[15px] gap-y-[50px] md:gap-x-[15px] md:gap-y-[50px] lg:gap-x-[20px] lg:gap-y-[50px]`}>
          {items.map((item, index) => {
            const currentValue = counts[index];
            // Verwendet jetzt dynamische Locale statt hardcoded "de-DE"
            const displayValue = currentValue.toLocaleString(locale, {
              minimumFractionDigits: item.decimals ?? 0,
              maximumFractionDigits: item.decimals ?? 0
            });

            // Ãœbersetze Suffix/Prefix falls vorhanden
            const translatedSuffix = translateAbbreviation(item.suffix, currentLanguage);
            const translatedPrefix = translateAbbreviation(item.prefix, currentLanguage);

            return (
              <div key={item.key} className="flex flex-col gap-3">
                <div className="flex items-center gap-[10px]">
                  <div className="w-[5px] h-full bg-[#37acdc]" />
                  <div className="text-3xl md:text-5xl lg:text-5xl font-bold text-white">
                    {translatedPrefix}
                    {displayValue}
                    {translatedSuffix}
                  </div>
                </div>
                
                <p className="text-white text-lg md:text-2xl lg:text-2xl font-serif">
                  {t(`counter.${item.key}.description`)}
                </p>
              </div>
            );
          })}
        </div>
      </Container>
    </section>
  );
};

export default Counter;